import { useEffect } from 'react';

const plans = [
  {
    id: 'hatchling',
    name: 'Starter Website',
    price: '$197',
    period: '/month',
    description: 'A simple website and lead capture setup for businesses that need a clean starting point.',
    features: [
      'Website design (1–3 pages)',
      'Hosting & support',
      'Logo & graphic design',
      'Lead capture form',
      'QR code form',
      'Basic email follow-up automation',
      'Custom domain email',
    ],
  },
  {
    id: 'nuggets',
    name: 'Growth Automation',
    price: '$997',
    period: '/month',
    description: 'The best fit for businesses that want missed calls handled, leads followed up, and appointments booked automatically.',
    badge: 'Most Popular',
    features: [
      'Website design & hosting support',
      'Voice AI assistant',
      'Conversation AI assistant',
      'Automated calendar / scheduling',
      'Social media automations',
      'Search Engine Optimization',
    ],
  },
  {
    id: 'titan',
    name: 'Scale & Ads',
    price: '$697',
    period: '/month',
    description: 'For businesses ready to add ads, reporting, and a more complete growth system.',
    features: [
      'Everything in Growth Automation',
      'Facebook & Google Ads management',
      'Advanced automation workflows',
      'Reputation management system',
      'Monthly performance reporting',
      'Priority support',
      'Monthly strategy call',
    ],
  },
];

interface PlanSelectorProps {
  selectedPlan: string;
  onSelectPlan: (planId: string) => void;
  addOnSelected: boolean;
  onToggleAddOn: (val: boolean) => void;
  onlineStoreSelected: boolean;
  onToggleOnlineStore: (val: boolean) => void;
}

export function PlanSelector({ selectedPlan, onSelectPlan, addOnSelected, onToggleAddOn, onlineStoreSelected, onToggleOnlineStore }: PlanSelectorProps) {
  useEffect(() => {
    if (!selectedPlan) {
      onSelectPlan('nuggets');
    }
  }, []);

  const selected = plans.find((p) => p.id === selectedPlan) || plans[1];

  return (
    <div className="space-y-4">
      <h3 className="font-display text-lg font-bold text-secondary mb-2">Select Your Plan</h3>
      <div className="space-y-3">
        {plans.map((plan) => {
          const isSelected = plan.id === selectedPlan;
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onSelectPlan(plan.id)}
              className={`w-full text-left rounded-xl p-5 border-2 transition-all duration-200 cursor-pointer ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'border-primary' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-secondary">{plan.name}</span>
                      {plan.badge && (
                        <span className="text-[10px] font-semibold bg-gradient-to-r from-primary to-secondary text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{plan.description}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <span className="text-2xl font-bold text-primary">{plan.price}</span>
                  <span className="text-sm text-gray-500">{plan.period}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected plan features */}
      <div className="mt-6 bg-gray-50 rounded-xl p-5">
        <h4 className="font-display font-semibold text-secondary text-sm mb-3">
          {selected.name} includes:
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {selected.features.map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-primary text-sm"></i>
              </div>
              <span className="text-xs text-gray-600 leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Business Launch Add-On */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => onToggleAddOn(!addOnSelected)}
          className={`w-full text-left rounded-xl p-5 border-2 transition-all duration-200 cursor-pointer ${
            addOnSelected
              ? 'border-secondary bg-secondary/5 shadow-md'
              : 'border-dashed border-gray-300 bg-white hover:border-secondary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                addOnSelected ? 'border-secondary bg-secondary' : 'border-gray-300'
              }`}
            >
              {addOnSelected && <i className="ri-check-line text-white text-xs"></i>}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-secondary text-sm">Business Launch Package</span>
                  <span className="text-[10px] font-semibold bg-secondary/10 text-secondary px-2 py-0.5 rounded-full whitespace-nowrap">
                    Add-On
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xl font-bold text-secondary">$997</span>
                  <span className="text-xs text-gray-500 ml-1">one-time</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                LLC formation, EIN / Federal Tax ID registration, business compliance setup, and guided onboarding — everything you need to legally launch.
              </p>
              {addOnSelected && (
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {[
                    'LLC Formation & Filing',
                    'EIN / Tax ID Registration',
                    'Business Compliance Setup',
                    'Banking Readiness Guide',
                    'Registered Agent Coordination',
                    'Guided Onboarding Support',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <i className="ri-check-line text-secondary text-xs flex-shrink-0"></i>
                      <span className="text-[11px] text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </button>
      </div>

      {/* Online Store Setup Add-On */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => onToggleOnlineStore(!onlineStoreSelected)}
          className={`w-full text-left rounded-xl p-5 border-2 transition-all duration-200 cursor-pointer ${
            onlineStoreSelected
              ? 'border-primary bg-primary/5 shadow-md'
              : 'border-dashed border-gray-300 bg-white hover:border-primary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                onlineStoreSelected ? 'border-primary bg-primary' : 'border-gray-300'
              }`}
            >
              {onlineStoreSelected && <i className="ri-check-line text-white text-xs"></i>}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-secondary text-sm">Online Store Setup</span>
                  <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full whitespace-nowrap">
                    Add-On
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xl font-bold text-primary">$997</span>
                  <span className="text-xs text-gray-500 ml-1">one-time</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Custom store design, product catalog, shopping cart, secure checkout, and payment integration — everything you need to sell online.
              </p>
              {onlineStoreSelected && (
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {[
                    'Custom Store Design',
                    'Product Catalog Setup',
                    'Shopping Cart & Checkout',
                    'Payment Integration',
                    'Mobile-Optimized',
                    'SEO-Ready Structure',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <i className="ri-check-line text-primary text-xs flex-shrink-0"></i>
                      <span className="text-[11px] text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

export { plans };
